import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import ApplyButton from './ApplyButton';
interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  toggleOpen: () => void;
  index: number;
}
const FAQItem = ({
  question,
  answer,
  isOpen,
  toggleOpen,
  index
}: FAQItemProps) => {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: index * 0.1
  }} className="border-b border-gray-800" data-id="element-48">
      <button onClick={toggleOpen} className="flex justify-between items-center w-full py-4 px-2 text-left focus:outline-none" data-id="element-49">
        <h4 className="text-white font-medium text-base sm:text-lg typekit-heading" data-id="element-50">
          {question}
        </h4>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.3
      }} data-id="element-51">
          <ChevronDownIcon className="w-5 h-5 text-[#ffdf07]" data-id="element-52" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-53">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-54">
            <div className="pb-4 px-2 text-gray-400 libre-caslon-text-regular" data-id="element-55">
              <p className="leading-relaxed" data-id="element-56">{answer}</p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
};
interface FAQSectionProps {
  scrollToCalendar?: () => void;
}
const FAQSection = ({
  scrollToCalendar
}: FAQSectionProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  const faqItems = [{
    question: "Is this for me if I'm under $3M or a solo clinic?",
    answer: "No. The 8‑Figure Clinic MRI™ is built specifically for owners of multi‑clinic groups doing roughly $3M–$25M+ a year who are still in build mode, not retirement mode. If you're smaller or just getting started, this will feel like overkill."
  }, {
    question: 'Do you raise capital or broker my sale?',
    answer: "No. We don't raise money, broker deals, or take success fees. We help you build the version of your business that serious money actually wants to back or buy later – so when you choose to engage, you're over‑qualified and in control."
  }, {
    question: "Can't my CPA do this?",
    answer: "Your tax CPA is great at filing and minimizing taxes. Buyers and investment committees rebuild your numbers completely differently. Our financial work is led by people who've done transaction diligence and valuation at firms like EY‑Parthenon, not by tax preparers."
  }, {
    question: 'How much of my time will this take?',
    answer: 'Over 14 days, expect: A kick‑off call, access to financials, ops docs, and data we request, 1–2 working sessions with our team, and a final results + plan call. We do the heavy lifting. You make decisions and give context.'
  }, {
    question: "What if I don't like what you find?",
    answer: "That's kind of the point. Most owners only find out what's \"wrong\" in a buyer meeting when it's too late to fix. We show you the truth while you still have time, energy, and control to change it – and we give you a path to do it."
  }, {
    question: 'Do you help implement the plan?',
    answer: "Often, yes – but it's not required. Some owners take the MRI outputs and have their internal team execute. Others ask us to help with financial prep, operational prep, growth systems, or data/payer upgrades via separate engagements. You don't have to decide that until after you've seen the MRI results."
  }, {
    question: 'What does it cost?',
    answer: "We'll walk through pricing and options on your Strategy Call once we know more about your size, complexity, and goals. What we can say here: if you're not confident we can move the needle on a potential 7–8 figure outcome, we won't invite you into the MRI."
  }];
  return <section id="faq" className="w-full bg-black py-16 px-4" data-id="element-57">
      <div className="max-w-3xl mx-auto" data-id="element-58">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7
      }} viewport={{
        once: true
      }} className="text-center mb-10" data-id="element-59">
          <h2 className="text-white text-3xl md:text-4xl mb-6 typekit-heading" data-id="element-60">
            Frequently Asked Questions
          </h2>
        </motion.div>
        <div className="mt-8" data-id="element-61">
          {faqItems.map((item, index) => <FAQItem key={index} question={item.question} answer={item.answer} isOpen={openIndex === index} toggleOpen={() => toggleFAQ(index)} index={index} data-id="element-62" />)}
        </div>
        <div className="mt-12" data-id="element-63">
          {scrollToCalendar ? <div className="flex justify-center" data-id="element-64">
              <div className="relative" data-id="element-65">
                <div className="button-border-light" data-id="element-66"></div>
                <motion.button onClick={scrollToCalendar} whileHover={{
              scale: 1.05,
              boxShadow: '0px 8px 20px rgba(255, 223, 7, 0.3)'
            }} className="relative z-10 bg-gradient-to-r from-[#ffdf07] via-[#fae651] to-[#ffdf07] text-black font-semibold py-3 px-8 sm:px-10 rounded-md text-base shadow-[0_4px_14px_rgba(255,223,7,0.25)] border border-[#ffd70077] transition-all duration-300" data-id="element-67">
                  Book My Strategy Call
                </motion.button>
              </div>
            </div> : <ApplyButton data-id="element-68" />}
        </div>
      </div>
    </section>;
};
export default FAQSection;